<script setup lang="ts">
import {type Ref, ref} from 'vue'
import {
  DateFormatter,
  getLocalTimeZone,
  type DateValue,
  now
} from '@internationalized/date'
import type {DateRange} from 'radix-vue'
import {useField} from "vee-validate";

import {cn} from '@/lib/utils'

const props = defineProps(['rules', 'startFieldName', 'endFieldName', 'placeholder', 'minDate']);

const start = defineModel('start', {required: false});
const end = defineModel('end', {required: false});

const today: DateValue = now('Asia/Dubai')

const df = new DateFormatter('en-US', {
  dateStyle: 'medium',
})

const { value: startField } = useField(props.startFieldName, props.rules);
const { value: endField } = useField(props.endFieldName, props.rules);

const value = ref({
  start: undefined,
  end: undefined,
}) as Ref<DateRange>

watch(() => value.value, (val) => {
  if (val) {
    start.value = startField.value = val.start?.toString();
    end.value = endField.value = val.end?.toString();
  }
})

</script>

<template>
  <UiPopover>
    <UiPopoverTrigger as-child>
      <UiButton
          variant="outline"
          :class="cn(
          'w-full justify-start text-left font-normal rounded-xl',
          !value && 'text-muted-foreground',
        )"
      >
        <Icon size="18" class="mr-2 text-gray-500" name="ri:calendar-2-line"/>
        <template v-if="value.start">
          <template v-if="value.end">
            {{ df.format(value.start.toDate(getLocalTimeZone())) }} - {{
              df.format(value.end.toDate(getLocalTimeZone()))
            }}
          </template>

          <template v-else>
            {{ df.format(value.start.toDate(getLocalTimeZone())) }}
          </template>
        </template>
        <template v-else>
          <span class="text-gray-500">{{ props.placeholder }}</span>
        </template>
      </UiButton>
    </UiPopoverTrigger>
    <UiPopoverContent class="w-auto p-0">
      <UiRangeCalendar
          v-model="value"
          :minValue="today"
          initial-focus
          :number-of-months="2"
          @update:start-value="(startDate) => value.start = startDate"
      />
    </UiPopoverContent>
  </UiPopover>
  <UiErrorMessage class="text-[13px] text-red-500" :name="`${startFieldName}`" /><br />
  <UiErrorMessage class="text-[13px] text-red-500" :name="`${endFieldName}`" />
</template>